import { selectHasPaymentMethod } from "@app/billing";
import {
  applySelectedPlan,
  fetchActivePlans,
  fetchPlans,
  selectFirstActivePlan,
  selectIsAccountOwner,
  selectPlanByActiveId,
  selectPlansForView,
  selectStacksByOrgAsList,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { TEAM_CONTACTS_PATH, homeUrl, logoutUrl, plansUrl } from "@app/routes";
import { capitalize } from "@app/string-utils";
import type { PlanName } from "@app/types";
import confetti from "canvas-confetti";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTrialNotice } from "../hooks/use-trial-notice";
import { HeroBgLayout } from "../layouts";
import {
  Banner,
  BannerMessages,
  Button,
  Group,
  IconLock,
  PlanIncludedFeaturesList,
  Plans,
  Tooltip,
  tokens,
} from "../shared";
import { AddPaymentMethodBlock, StripeProvider } from "./billing-method";

const PaymentSuccessView = ({
  planName,
  plan,
  onReturnToDashboard,
}: {
  planName: string;
  plan: { name: PlanName };
  onReturnToDashboard: () => void;
}) => {
  const planLabel = `${capitalize(planName)} plan`;

  useEffect(() => {
    const colors = ["#E09600", "#4361FF", "#22c55e"];
    // Left side: burst from left edge, angled toward center
    confetti({
      particleCount: 60,
      spread: 60,
      origin: { x: 0, y: 0.5 },
      angle: 45,
      colors,
    });
    // Right side: burst from right edge, angled toward center
    confetti({
      particleCount: 60,
      spread: 60,
      origin: { x: 1, y: 0.5 },
      angle: 135,
      colors,
    });
    const t = setTimeout(() => {
      confetti({
        particleCount: 40,
        spread: 50,
        origin: { x: 0, y: 0.55 },
        angle: 30,
        colors,
      });
      confetti({
        particleCount: 40,
        spread: 50,
        origin: { x: 1, y: 0.55 },
        angle: 150,
        colors,
      });
    }, 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <Group className="items-center max-w-xl mx-auto">
      <h1 className={`${tokens.type.h1} text-center`}>
        Welcome to the {planLabel}!
      </h1>
      <p className="text-base font-normal text-gray-700 mt-2">
        You&apos;ve successfully upgraded your plan. You now have access to:
      </p>
      <div className="w-full rounded-lg border border-gray-200 bg-white p-4">
        <PlanIncludedFeaturesList plan={plan} />
      </div>
      <Button onClick={onReturnToDashboard} className="mt-6">
        Return to Dashboard
      </Button>
    </Group>
  );
};

export const PlansPage = () => {
  const dispatch = useDispatch();
  const org = useSelector(selectOrganizationSelected);
  const isAccountOwner = useSelector((s) =>
    selectIsAccountOwner(s, { orgId: org.id }),
  );

  const activePlanLoader = useQuery(fetchActivePlans({ orgId: org.id }));
  const activePlan = useSelector(selectFirstActivePlan);

  const planLoader = useQuery(fetchPlans());
  const plans = useSelector(selectPlansForView);
  const selectedPlan = useSelector((s) =>
    selectPlanByActiveId(s, { id: activePlan.planId }),
  );
  const applyPlanLoader = useLoader(applySelectedPlan);
  const { hasTrialNoPayment } = useTrialNotice();
  const hasPaymentMethod = useSelector(selectHasPaymentMethod);
  const paymentRequired = hasTrialNoPayment || !hasPaymentMethod;

  const [step, setStep] = useState<1 | 2>(1);
  const [pendingSelectedPlan, setPendingSelectedPlan] = useState<{
    planId: string;
    name: string;
  } | null>(null);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);
  const [cardSaved, setCardSaved] = useState(false);
  const planForSuccessRef = useRef<{ planId: string; name: string } | null>(
    null,
  );

  const stacks = useSelector(selectStacksByOrgAsList);
  const hasDedicatedStack = stacks.filter((s) => s.organizationId).length > 0;
  const legacy = hasDedicatedStack && !activePlan.planId;

  useLoaderSuccess(applyPlanLoader, () => {
    const plan = planForSuccessRef.current;
    if (plan) {
      setPendingSelectedPlan(plan);
      setShowUpgradeSuccess(true);
      planForSuccessRef.current = null;
    }
  });

  const dispatchApplySelectedPlan = (plan: {
    planId: string;
    name: string;
  }) => {
    planForSuccessRef.current = plan;
    dispatch(
      applySelectedPlan({
        orgId: org.id,
        planId: plan.planId,
        name: plan.name,
      }),
    );
  };

  if (!isAccountOwner) {
    return (
      <HeroBgLayout width={1200} showOrgPicker>
        <Group>
          <h1 className={`${tokens.type.h1} text-center`}>Plans</h1>

          <Group className="items-center">
            <div className="max-w-2xl">
              <div className="flex items-center justify-center gap-2 p-8 bg-gray-50 rounded-lg">
                <IconLock variant="sm" className="opacity-60" />
                <div className="text-center">
                  <p className="text-gray-600">
                    You must be an account owner to view and manage plans.
                  </p>
                  <Tooltip text="Must be account owner" fluid>
                    <span className="text-sm text-gray-500 cursor-help">
                      Contact your account owner for access to this page.
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </Group>
        </Group>
      </HeroBgLayout>
    );
  }

  if (showUpgradeSuccess && pendingSelectedPlan) {
    return (
      <HeroBgLayout width={1200} showOrgPicker>
        <Group>
          <PaymentSuccessView
            planName={pendingSelectedPlan.name}
            plan={plans[pendingSelectedPlan.name as keyof typeof plans]}
            onReturnToDashboard={() => {
              window.location.href = homeUrl();
            }}
          />
        </Group>
      </HeroBgLayout>
    );
  }

  if (paymentRequired && step === 2 && pendingSelectedPlan) {
    return (
      <HeroBgLayout width={1200} showOrgPicker>
        <Group>
          <h1 className={`${tokens.type.h1} text-center`}>
            Add payment method
          </h1>
          <p className="text-center text-gray-600 max-w-xl mx-auto">
            You must enter a credit card to continue. Your card will be charged
            at the end of your monthly billing cycle.
          </p>
          <BannerMessages {...applyPlanLoader} />
          <StripeProvider>
            <div className="max-w-[500px] mx-auto">
              <AddPaymentMethodBlock
                selectedPlan={pendingSelectedPlan}
                changePlanHref={plansUrl()}
                planChangeLoading={applyPlanLoader.isLoading}
                onSelectDifferentPlan={() => {
                  setStep(1);
                  setPendingSelectedPlan(null);
                }}
                onSuccess={() => {
                  setCardSaved(true);
                  setStep(1);
                  dispatchApplySelectedPlan(pendingSelectedPlan);
                }}
              />
            </div>
          </StripeProvider>
        </Group>
      </HeroBgLayout>
    );
  }

  return (
    <HeroBgLayout width={1200} showOrgPicker>
      <Group>
        <h1 className={`${tokens.type.h1} text-center`}>Choose a Plan</h1>

        <Group className="items-center">
          <div className="max-w-2xl">
            <p>
              If your trial has expired, choose a plan to continue or{" "}
              <Link to={logoutUrl()}>Log Out</Link>
            </p>
          </div>
        </Group>

        <Group>
          <BannerMessages {...applyPlanLoader} />
          {isAccountOwner && <BannerMessages {...planLoader} />}
          {isAccountOwner && <BannerMessages {...activePlanLoader} />}
          {legacy ? (
            <Banner>
              You are currently on an older plan with Aptible. Please{" "}
              <Link to="https://aptible.com/contact">contact us</Link> to update
              your plan or{" "}
              <Link to="https://www.aptible.com/pricing">view new plans</Link>.
            </Banner>
          ) : null}
        </Group>

        {legacy ? null : (
          <Plans
            activePlan={activePlan}
            selected={selectedPlan.name}
            onSelectPlan={dispatchApplySelectedPlan}
            paymentRequired={paymentRequired}
            onSelectPlanWhenPaymentRequired={
              paymentRequired
                ? (planId, name) => {
                    const plan = { planId, name };
                    setPendingSelectedPlan(plan);
                    if (cardSaved) {
                      dispatchApplySelectedPlan(plan);
                      return;
                    }
                    setStep(2);
                  }
                : undefined
            }
          />
        )}

        <div className="text-center mt-8 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Looking to delete your account?{" "}
            <Link
              to={TEAM_CONTACTS_PATH}
              className="text-indigo-600 hover:text-indigo-800 underline"
            >
              Click here
            </Link>
          </p>
        </div>
      </Group>
    </HeroBgLayout>
  );
};
